import { AnimationBuilder, AnimationPlayer } from '@angular/animations';
import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
  Renderer2,
  ViewChild
} from '@angular/core';
import {
  HEADER_MENU,
  HEADER_TOP_MENU,
  LoggingService
} from '@com-delta-idp/mnx-utils';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataLayer } from '../../common/datalayer/datalayer';
import { ApplicationConfig } from '../../config/config';
import { FlyoutSearchComponent } from '../../flyout/flyout-search/flyout-search.component';
import { AdobeDataLayerService } from '../../services/adobe-data-layer/adobe-data-layer';
import { CommonService } from '../../services/common.service';

@Component({
  selector: 'ngc-search',
  templateUrl: 'search.component.html'
})
export class SearchComponent implements OnDestroy {
  @ViewChild('triggerElement') triggerElement: ElementRef;
  @Input()
  avoidFocus: boolean;

  @Input()
  modalPosition: string;

  @Input()
  origin: string;

  @Input()
  isAlertAdvisory: boolean;

  @Input() searchText: string;

  @Output() modalServiceRef = new EventEmitter<any>();
  modalServiceOnHiddenSubscribe: Subscription;
  bsModalRef: BsModalRef;
  config = {
    animated: false,
    ignoreBackdropClick: true,
    keyboard: false
  };

  public player: AnimationPlayer;
  private unsubscribe: Subject<void> = new Subject();

  constructor(
    private modalService: BsModalService,
    public el: ElementRef,
    private renderer: Renderer2,
    private changeDetection: ChangeDetectorRef,
    private datalayer: DataLayer,
    private _builder: AnimationBuilder,
    private appconfig: ApplicationConfig,
    private srv: CommonService,
    private loggingService: LoggingService,
    private adobeDataLayer: AdobeDataLayerService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  open() {
    try {
      this.datalayer.setTrackingObjectProp1(
        HEADER_MENU.SEARCH.PAGE_SECTION,
        this.triggerElement.nativeElement
      );
      this.adobeDataLayer.updateAnalytics('search', HEADER_TOP_MENU.SEARCH);

      const bodyScroll = document.documentElement.scrollTop;
      let obj;
      if (this.modalPosition) {
        if (this.origin) {
          obj = Object.assign({}, this.config, {
            class:
              this.modalPosition +
              ' ' +
              this.origin +
              ' ' +
              'footer-search-modal hp-footer'
          });
        } else {
          obj = Object.assign({}, this.config, { class: this.modalPosition });
        }
      } else {
        if (this.isAlertAdvisory) {
          obj = Object.assign({}, this.config, {
            class:
              'loggedIn-modal-dialog loggedIn-alertAdv-modal-dialog no-transform'
          });
        } else {
          obj = Object.assign({}, this.config, {
            class: 'loggedIn-modal-dialog no-transform'
          });
        }
      }
      this.bsModalRef = this.modalService.show(FlyoutSearchComponent, obj);
      if (this.appconfig.get('APP_DISABLE_LEGACY') === 'false') {
        document
          .getElementsByTagName('modal-container')[0]
          .classList.add('d-block');
      }
      if (this.bsModalRef) {
        if (isPlatformBrowser(this.platformId)) {
          if (!this.modalPosition) {
            if (document.body.scrollWidth >= 992) {
              this.bsModalRef.content.headerSearchModalLoaded = true;
              const modalContainer =
                document.getElementsByTagName('modal-container')[0];
              modalContainer.classList.add('search-header-modal');
            } else {
              this.bsModalRef.content.headerSearchModalLoaded = true;
            }
          } else {
            if (document.body.scrollWidth >= 992) {
              this.bsModalRef.content.footerSearchModalLoaded = true;
              document
                .getElementsByTagName('modal-container')[0]
                .classList.add('search-footer-modal');
            } else {
              this.bsModalRef.content.headerSearchModalLoaded = true;
            }
          }
        }
        // this.bsModalRef.content.dialog.nativeElement.focus();
        this.bsModalRef.content.searchField.nativeElement.focus();
        this.modalServiceRef.emit(this.bsModalRef);
        // this.bsModalRef.content.searchField.nativeElement.focus();
        if (this.bsModalRef.content.dialog.nativeElement.offsetParent != null) {
          if (!this.modalPosition) {
            const parentContainer =
              this.bsModalRef.content.dialog.nativeElement.offsetParent
                .children[0];
            parentContainer.className += ' loggedIn-modal-height';
          }
        }
      }
      const htmlRoot = document.getElementsByTagName('html')[0];
      if (!(htmlRoot.className.indexOf('no-scroll') >= 0)) {
        htmlRoot.className += ' no-scroll';
      }

      this.modalServiceOnHiddenSubscribe = this.modalService.onHidden
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (reason: string) => {
            this.origin == 'footer'
              ? (
                  document.getElementsByClassName(
                    'search-text'
                  )[0] as HTMLElement
                )?.focus()
              : this.triggerElement.nativeElement.focus();
            this.modalServiceRef.emit(null);
            this.modalServiceOnHiddenSubscribe.unsubscribe();
          },
          (err) => {
            this.loggingService.log('Caught Error: ' + err);
          }
        );
      this.srv.dragModalContainers(this.el, this.renderer);
      document.documentElement.scrollTop = bodyScroll;
      this.srv.focusModalContainers(this.el, this.renderer);
    } catch (err) {
      this.loggingService.log('Caught Error: ' + err);
    }
  }

  handleFocusEvent(event) {
    if (event.keyCode === 9) {
      this.triggerElement.nativeElement.blur();
    }
  }

  getSearchTopics(): string[] {
    try {
      if (this.bsModalRef && this.bsModalRef.content) {
        const topicElements = document.querySelectorAll('.search-topic');
        return Array.from(topicElements).map(
          (el) => el.textContent?.trim() || ''
        );
      }
    } catch (err) {
      this.loggingService.log('Error getting search topics: ' + err);
    }
    return [];
  }

  ngOnDestroy() {
    if (this.modalServiceOnHiddenSubscribe) {
      this.modalServiceOnHiddenSubscribe.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribe.unsubscribe();
  }
}
